import * as Sentry from '@sentry/browser'
import { AuthApi, ErrorCodeEnum } from '@ulysses-inc/harami_api_client'
import {
  getRequestError,
  isAppVersionInconsistentError,
} from '~/adapter/api/error'
import { useAuthApi } from '~/adapter/api/useAuthApi'
import { useClearAllPersistedData } from '~/app/hooks/useClearAllPersistedData'
import { routes } from '~/app/routes/paths'
import { appMode } from '~/config'
import type { AdditionalMessagePattern } from '~/pages/RumpLogin/RumpLogin'
import { useAppVersionConsistency } from '~/stores/appVersionConsistency/appVersionConsistency'
import {
  kioskAuthErrorKindFromErrorCode,
  useKioskAuthErrorStore,
} from '~/stores/kioskAuthError/kioskAuthError'
import { ErrorWithExtra } from '~/utils/error'
import { replaceLocation } from '~/utils/navigation'

export const useLogout = () => {
  const { getApi } = useAuthApi()
  const inconsistentVersionDetected = useAppVersionConsistency(
    state => state.inconsistentVersionDetected,
  )
  const { clearAllPersistedData } = useClearAllPersistedData()
  const kioskAuthErrorDetected = useKioskAuthErrorStore(
    state => state.kioskAuthErrorDetected,
  )

  const logout = async ({
    userInitiated,
    additionalMessage,
    errorCode,
  }: {
    userInitiated: boolean
    additionalMessage?: AdditionalMessagePattern
    errorCode?: ErrorCodeEnum
  }) => {
    try {
      await getApi(AuthApi).authLogout()
    } catch (e) {
      if (isAppVersionInconsistentError(await getRequestError(e))) {
        inconsistentVersionDetected()
        // 本エラーが発生している場合には、バックエンドでのログアウト処理自体が実行されていないので、
        // localStorage のクリアや redirect などの処理は行わない
        return
      }

      // NOTE: サーバーのログアウト処理に失敗した場合でも、ネットワークエラーなどを除いて Cookie は削除されるはずのため、ログアウト処理は継続させる
      Sentry.captureException(
        new ErrorWithExtra(
          'AuthLogoutFailure',
          'ログアウトのAPIリクエストに失敗しました',
          {
            originalError: e,
          },
        ),
      )
    }

    clearAllPersistedData()

    // キオスク端末（kiosk）にはログインの概念が無いため /login へは遷移せず、
    // その場に全画面エラー（KioskAuthError）を被せる。
    // / へ遷移させると、原因が恒久的（audience 不足など）な場合に
    // 「/ → セッション再作成 → /me 失敗 → logout → / …」のリロードループになる。
    if (appMode.isKiosk) {
      kioskAuthErrorDetected(kioskAuthErrorKindFromErrorCode(errorCode), {
        reportSavedOffline: additionalMessage === 'offline_saved',
      })
      return
    }

    // logout時にbrowser back利用して欲しくないためlocation.replaceを利用
    const defaultParams = additionalMessage ? { additionalMessage } : {}
    const route = userInitiated
      ? routes.login.buildPath({ queryParams: defaultParams })
      : routes.login.buildPath({
          queryParams: {
            errorCode: errorCode ?? ErrorCodeEnum.AuthenticationExpired,
            ...defaultParams,
          },
        })
    replaceLocation(route)
  }

  return { logout }
}
